import React, { forwardRef } from 'react'
import styled from 'styled-components'
import { color } from 'styles/colors'
import { IconButton } from '@mui/material'
import GA4 from 'helpers/ga4'

type Props = {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  isConnected: boolean
  canSend: boolean
}

const ChatInputBar = forwardRef<HTMLTextAreaElement, Props>(
  ({ value, onChange, onSend, isConnected, canSend }, ref) => {
    const handleSend = () => {
      if (canSend) {
        GA4.sendEvent({ category: 'Chat Bot', action: 'Chat Bot Send Message' })
      }
      onSend()
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    }

    return (
      <S.Container>
        <S.Input
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask me anything about product literature and equipment information"
          disabled={!isConnected}
          rows={1}
        />
        <S.VoiceButton>
          <img
            src={isConnected ? '/img/microphone-lines.svg' : '/img/microphone-lines-disabled.png'}
            alt="Voice Chat"
          />
        </S.VoiceButton>
        <S.SendButton onClick={handleSend} disabled={!canSend}>
          <img
            src={canSend ? '/img/circle-up.svg' : '/img/circle-up-disabled.svg'}
            alt="Send"
          />
        </S.SendButton>
      </S.Container>
    )
  }
)

ChatInputBar.displayName = 'ChatInputBar'

export default ChatInputBar

const S = {
  Container: styled.div`
    display: flex;
    align-items: center;
    padding: 4px 4px 4px 16px;
    background: ${color('chatbotInputBackground')};
    border-radius: 20px;
    flex-shrink: 0;
    width: 100%;
    box-sizing: border-box;
  `,
  Input: styled.textarea`
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    box-shadow: none;
    padding: 8px 0;
    font-size: 14px;
    font-family: inherit;
    resize: none;
    field-sizing: content;
    &:focus {
      background: transparent;
      border: none;
      outline: none;
      box-shadow: none;
    }
    &:disabled {
      opacity: 0.5;
    }
  `,
  SendButton: styled(IconButton)`
    height: 24px;
    width: 24px;
    padding: 0;
    margin: 0 8px;
  `,
  VoiceButton: styled(IconButton)`
    height: 28px;
    width: 28px;
  `
}
